import { signal, WritableSignal, effect, Injector } from '@angular/core';

export interface LinkedSignalOptions<S, D> {
    source: () => S;
    computation: (newSourceValue: S, previous?: { source: S; value: D }) => D;
    injector?: Injector;
}

/**
 * Added to support linkedSignals which are not available in angular 18.
 * See https://angular.dev/api/core/linkedSignal
 * Can be removed once migrated to angular v19+
 */
export function ng18_createLinkedSignal<S, D>(
    options: LinkedSignalOptions<S, D>
): WritableSignal<D> {
    const { source, computation, injector } = options;

    let lastSourceValue: S = source();
    let lastLinkedValue: D = computation(lastSourceValue);

    const resultSignal = signal<D>(lastLinkedValue);

    effect(
        () => {
            const currentSource = source();

            if (currentSource !== lastSourceValue) {
                const previousContext = {
                    source: lastSourceValue,
                    value: lastLinkedValue
                };

                const newValue = computation(currentSource, previousContext);

                lastSourceValue = currentSource;
                lastLinkedValue = newValue;

                resultSignal.set(newValue);
            }
        },
        {
            allowSignalWrites: true,
            injector: injector
        }
    );

    const wrappedSignal = (() => {
        return resultSignal();
    }) as unknown as WritableSignal<D>;

    Object.setPrototypeOf(wrappedSignal, Object.getPrototypeOf(resultSignal));

    wrappedSignal.set = (value: D) => {
        lastLinkedValue = value;
        resultSignal.set(value);
    };

    wrappedSignal.update = (fn: (value: D) => D) => {
        resultSignal.update((current) => {
            const updated = fn(current);
            lastLinkedValue = updated;
            return updated;
        });
    };

    wrappedSignal.asReadonly = () => resultSignal.asReadonly();

    return wrappedSignal;
}
